import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { BillingPeriodToggle } from '../../BillingPeriodToggle';
import { LabelCaps } from '../LabelCaps';
import type { BillingPeriod } from '../../../utils/paymentTiers';
import type { CatalogRelationship } from '../../../utils/subscriptionApi';

const RELATIONSHIPS: CatalogRelationship[] = ['Estate', 'Family', 'Collaborator', 'Represented by', 'Other'];

/**
 * Suggest a 2-letter ALI ID prefix from the creator's name — mirrors the
 * backend ``derive_initials`` so the pre-filled value matches what the server
 * would otherwise pick. Two+ words → first letter of first + first letter of
 * last; a single word → its first two letters. Non-letters are skipped.
 */
function suggestInitials(creatorName: string): string {
  const tokens = creatorName
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const firstLetter = (token: string): string => {
    const match = token.match(/[A-Za-z]/);
    return match ? match[0].toUpperCase() : '';
  };
  if (tokens.length >= 2) {
    return (firstLetter(tokens[0]) + firstLetter(tokens[tokens.length - 1])).slice(0, 2);
  }
  if (tokens.length === 1) {
    const letters = tokens[0].replace(/[^A-Za-z]/g, '').toUpperCase();
    return letters.slice(0, 2);
  }
  return '';
}

const INITIALS_RE = /^[A-Z]{2}$/;

interface AddCatalogDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Submits the new catalog request. Returns when the Stripe Checkout
   * Session has been created and the redirect is about to fire — the
   * dialog stays open for that brief window so the spinner reads.
   */
  onSubmit: (input: {
    creatorName: string;
    relationship: CatalogRelationship;
    billingPeriod: BillingPeriod;
    initials: string;
  }) => Promise<void>;
  defaultBillingPeriod?: BillingPeriod;
}

export const AddCatalogDialog: React.FC<AddCatalogDialogProps> = ({
  open,
  onClose,
  onSubmit,
  defaultBillingPeriod = 'monthly',
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [creatorName, setCreatorName] = useState('');
  const [initials, setInitials] = useState('');
  const [initialsEdited, setInitialsEdited] = useState(false);
  const [relationship, setRelationship] = useState<CatalogRelationship>('Estate');
  const [addonCycle, setAddonCycle] = useState<BillingPeriod>(defaultBillingPeriod);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Reset whenever the dialog re-opens. The cascading-render warning is
  // intentional — one render per open transition is the design.
  useEffect(() => {
    if (open) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setCreatorName('');
      setInitials('');
      setInitialsEdited(false);
      setRelationship('Estate');
      setAddonCycle(defaultBillingPeriod);
      setError(null);
      setSubmitting(false);
      setConfirmCancel(false);
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [open, defaultBillingPeriod]);

  const price = addonCycle === 'yearly' ? '$120' : '$15';
  const per = addonCycle === 'yearly' ? '/year' : '/month';
  const initialsValid = INITIALS_RE.test(initials);
  const isDirty = creatorName.trim().length > 0 || initialsEdited;

  const handleCreatorNameChange = (value: string) => {
    setCreatorName(value);
    // Keep the suggested prefix in sync until the user takes it over.
    if (!initialsEdited) {
      setInitials(suggestInitials(value));
    }
  };

  const handleInitialsChange = (value: string) => {
    setInitialsEdited(true);
    setInitials(value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2));
  };

  // Confirm before discarding in-progress details; close immediately when the
  // form is pristine. Never interrupt an in-flight redirect to Stripe.
  const requestClose = () => {
    if (submitting) return;
    if (isDirty) {
      setConfirmCancel(true);
      return;
    }
    onClose();
  };

  const handleSubmit = async () => {
    if (!creatorName.trim() || !initialsValid) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        creatorName: creatorName.trim(),
        relationship,
        billingPeriod: addonCycle,
        initials,
      });
      // No onClose() — the parent triggers a window redirect to Stripe.
    } catch (err) {
      setError((err as Error).message || 'Failed to start checkout.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={requestClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
        Add New Catalog
        <IconButton size="small" onClick={requestClose} aria-label="Close" disabled={submitting}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a catalog for another creator. Each catalog has its own ALI ID, billing cycle, and payment method.
        </Typography>

        <TextField
          label="Creator Name"
          required
          fullWidth
          value={creatorName}
          onChange={(e) => handleCreatorNameChange(e.target.value)}
          placeholder="e.g. Vera Ostroff"
          helperText="The person whose artistic legacy this catalog represents."
          sx={{ mb: 2.5 }}
        />

        <TextField
          label="ALI ID Initials (2 letters)"
          required
          value={initials}
          onChange={(e) => handleInitialsChange(e.target.value)}
          placeholder="e.g. VO"
          // Surface the error once the user has typed a name too — otherwise a
          // name we can't derive 2 letters from (a mononym, or a non-Latin
          // name) leaves the field blank with no hint and the submit silently
          // disabled.
          error={!initialsValid && (initialsEdited || creatorName.trim().length > 0)}
          helperText={
            !initialsValid && (initialsEdited || creatorName.trim().length > 0)
              ? "Enter exactly 2 letters for this catalog's ALI ID prefix."
              : `This catalog's ALI ID will start with these initials, e.g. ${
                  initialsValid ? initials : 'VO'
                }-3K7. Confirm or change them.`
          }
          inputProps={{ maxLength: 2, style: { textTransform: 'uppercase', letterSpacing: '0.15em' } }}
          sx={{ mb: 2.5, width: 220 }}
        />

        <Box sx={{ mb: 2.5 }}>
          <LabelCaps>Your Relationship</LabelCaps>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {RELATIONSHIPS.map((r) => {
              const selected = relationship === r;
              return (
                <Button
                  key={r}
                  variant={selected ? 'outlined' : 'text'}
                  onClick={() => setRelationship(r)}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderColor: selected ? 'primary.main' : 'divider',
                    border: '1px solid',
                    color: selected ? 'primary.main' : 'text.primary',
                    bgcolor: selected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                    fontWeight: 500,
                  }}
                >
                  {r}
                </Button>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.16),
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box>
              <LabelCaps>Additional Catalog</LabelCaps>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.5 }}>
                You'll be redirected to Stripe to enter (or pick) a payment method for this catalog. You can use a different
                card than your other catalogs.
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ mb: 0, whiteSpace: 'nowrap' }}>
              {price}
              <Box
                component="span"
                sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 400, fontFamily: theme.typography.fontFamily, ml: 0.25 }}
              >
                {per}
              </Box>
            </Typography>
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <BillingPeriodToggle value={addonCycle} onChange={setAddonCycle} />
          </Box>
        </Box>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={requestClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!creatorName.trim() || !initialsValid || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Redirecting…' : 'Add Catalog & Pay'}
        </Button>
      </DialogActions>

      {/* Confirm before discarding in-progress catalog details. */}
      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)} maxWidth="xs">
        <DialogTitle>Discard new catalog?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Your details for this catalog haven't been saved. Are you sure you want to cancel?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel(false)}>Keep editing</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmCancel(false);
              onClose();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
